<template>
  <v-container v-if="fund" class="report break-word">
    <v-app-bar app color="secondary" dark dense>
      <v-toolbar-title>
        {{ fund.name }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-btn
        :to="{
          name: 'auditAuditPeriod',
          params: { id: fund.auditPeriod.id },
        }"
      >
        {{ $t("auditor.stepModelRequirements.back") }}
      </v-btn>
    </v-app-bar>

    <v-row>
      <v-col v-if="type === 'provisional'" cols="12">
        <v-alert type="info" block>
          {{ $t("fund.report.provisionalInfo") }}
        </v-alert>
      </v-col>

      <v-col cols="12">
        <v-btn color="primary" @click="downloadPdf" :loading="loading">
          {{ $t("fund.report.downloadPdf") }}
        </v-btn>
      </v-col>

      <v-col v-if="audit" cols="12">
        <FundReportForm :fund="fund" />
      </v-col>

      <v-col cols="12">
        <h1>
          {{ reportTitle }}
        </h1>
      </v-col>

      <v-col cols="12">
        <DataTable :fund="fund" />
      </v-col>

      <v-col cols="12">
        <ReportSummary :fund="fund" :audit="audit" />
      </v-col>

      <v-col cols="12">
        <MinimalRequirements
          :requirements="fund.report.minimalRequirements"
          :audit="audit"
          :audituser="audituser"
          :type="type"
          :fund="fund"
        />
      </v-col>

      <v-col v-if="fund.report.stepModelCategories" cols="12">
        <StepModelRequirements
          :fund="fund"
          :categories="fund.report.stepModelCategories"
          :audit="audit"
          :audituser="audituser"
          :audited="fund.report.minimalRequirementsResult === 'PASS'"
          :type="type"
        />
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-text>
            <Markdown :value="disclaimer" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import getPdfMake from "@/utils/getPdfMake";
import DataTable from "@/components/Fund/DataTable";
import FundReportQuery from "@/gql/queries/FundReportQuery.gql";
import ReportSummary from "@/components/User/Fund/Report/ReportSummary";
import generateReportPdfData from "@/utils/Report/generateReportPdfData";
import generateReportPdfData2022 from "@/utils/Report/2022/generateReportPdfData";
import generateReportPdfData2023 from "@/utils/Report/2023/generateReportPdfData";
import generateReportPdfData2024 from "@/utils/Report/2024/generateReportPdfData";
import generateReportPdfData2025 from "@/utils/Report/2025/generateReportPdfData";
import MinimalRequirements from "@/components/User/Fund/Report/MinimalRequirements";
import StepModelRequirements from "@/components/User/Fund/Report/StepModelRequirements";
import FundReportForm from "../../Audit/Fund/FundReportForm.vue";
import { getDisclaimer } from "@/utils/getDisclaimer";

export default {
  components: {
    DataTable,
    ReportSummary,
    MinimalRequirements,
    StepModelRequirements,
    FundReportForm,
  },

  props: {
    id: {
      type: String,
      required: true,
    },

    audit: {
      type: Boolean,
      default: false,
    },

    audituser: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: "final",
    },
  },

  data() {
    return {
      fund: null,
      loading: false,
    };
  },

  apollo: {
    fund: {
      query: FundReportQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          id: this.id,
        };
      },
    },
  },

  computed: {
    disclaimer() {
      return getDisclaimer(this.fund);
    },

    reportTitle() {
      if (this.fund.reportTitle) return this.fund.reportTitle;

      if (this.fund.precheck) {
        return this.$t("fund.report.title.precheck", { year: this.fund.auditPeriod.year + 1 });
      }

      return this.$t("fund.report.title.final", { year: this.fund.auditPeriod.year + 1 });
    },
  },

  methods: {
    async downloadPdf() {
      this.loading = true;

      const pdfMake = await getPdfMake();
      const user = this.audit ? "auditor" : "user";
      const prefix = this.$t(`fund.report.${this.type}.file.name.${user}`);

      let pdfData = null;

      if (this.fund.auditPeriod.year >= 2025) {
        pdfData = generateReportPdfData2025(this.fund, this.type, this.audit);
      } else if (this.fund.auditPeriod.year >= 2024) {
        pdfData = generateReportPdfData2024(this.fund, this.type, this.audit);
      } else if (this.fund.auditPeriod.year >= 2023) {
        pdfData = generateReportPdfData2023(this.fund, this.type, this.audit);
      } else if (this.fund.auditPeriod.year >= 2022) {
        pdfData = generateReportPdfData2022(this.fund, this.type, this.audit);
      } else {
        pdfData = generateReportPdfData(this.fund, this.type, this.audit);
      }

      pdfMake.createPdf(pdfData).download(`${prefix}${this.fund.auditPeriod.year + 1}_${this.fund.name}.pdf`);

      this.loading = false;
    },
  },
};
</script>
